// Mapping of domain states to presentation (tone + icon + i18n key). Tones are never the
// only signal: every pill renders an icon and a text label.
import type { ProjectState, RequirementState, TaskStatus } from "@maestros/contracts";
import type { ProjectSummary } from "@maestros/contracts";
import type { MessageKey } from "../i18n/en.ts";

export type Tone = "success" | "warning" | "danger" | "neutral" | "info";
/**
 * Icon of a state. "spinner" is the only animated one and is reserved for transient operations in
 * flight (a request being processed); domain states that merely are active (in analysis, in
 * progress, running) use static icons so boards and column headers never spin.
 */
export type IconKind = "check" | "x" | "alert" | "clock" | "spinner" | "pause" | "ban" | "circle" | "dot" | "search" | "play" | "activity" | "merge";

export interface StatusView {
  tone: Tone;
  icon: IconKind;
  label: MessageKey;
}

export type AggregateState = ProjectSummary["aggregate_state"];

export const AGGREGATE_VIEW: Record<AggregateState, StatusView> = {
  not_started: { tone: "neutral", icon: "circle", label: "aggregate.not_started" },
  running: { tone: "info", icon: "play", label: "aggregate.running" },
  needs_attention: { tone: "warning", icon: "alert", label: "aggregate.needs_attention" },
  blocked: { tone: "danger", icon: "ban", label: "aggregate.blocked" },
  completed: { tone: "success", icon: "check", label: "aggregate.completed" },
  paused: { tone: "neutral", icon: "pause", label: "aggregate.paused" },
};

export const PROJECT_STATE_VIEW: Record<ProjectState, StatusView> = {
  draft: { tone: "neutral", icon: "circle", label: "projectState.draft" },
  active: { tone: "info", icon: "dot", label: "projectState.active" },
  paused: { tone: "neutral", icon: "pause", label: "projectState.paused" },
  draining: { tone: "warning", icon: "clock", label: "projectState.draining" },
  stopped: { tone: "neutral", icon: "ban", label: "projectState.stopped" },
};

export const TASK_STATUS_VIEW: Record<TaskStatus, StatusView> = {
  backlog: { tone: "neutral", icon: "circle", label: "status.backlog" },
  in_analysis: { tone: "info", icon: "search", label: "status.in_analysis" },
  todo: { tone: "neutral", icon: "circle", label: "status.todo" },
  in_progress: { tone: "info", icon: "play", label: "status.in_progress" },
  testing: { tone: "info", icon: "activity", label: "status.testing" },
  in_review: { tone: "info", icon: "clock", label: "status.in_review" },
  integrating: { tone: "info", icon: "merge", label: "status.integrating" },
  awaiting_release: { tone: "neutral", icon: "clock", label: "status.awaiting_release" },
  done: { tone: "success", icon: "check", label: "status.done" },
  blocked: { tone: "danger", icon: "ban", label: "status.blocked" },
  waiting_resource: { tone: "warning", icon: "clock", label: "status.waiting_resource" },
  paused: { tone: "neutral", icon: "pause", label: "status.paused" },
  failed: { tone: "danger", icon: "x", label: "status.failed" },
  cancelled: { tone: "neutral", icon: "ban", label: "status.cancelled" },
  awaiting_human: { tone: "warning", icon: "clock", label: "status.awaiting_human" },
};

/** Work-in-progress step state (RF-UI-12), derived from task facts. */
export type StepState = "waiting" | "in_progress" | "done" | "failed" | "blocked" | "paused" | "cancelled";

export function stepStateOf(status: string): StepState {
  switch (status) {
    case "done":
      return "done";
    case "failed":
      return "failed";
    case "blocked":
      return "blocked";
    case "paused":
      return "paused";
    case "cancelled":
      return "cancelled";
    case "in_analysis":
    case "in_progress":
    case "testing":
    case "in_review":
    case "integrating":
      return "in_progress";
    default:
      return "waiting";
  }
}

export const STEP_VIEW: Record<StepState, StatusView> = {
  waiting: { tone: "neutral", icon: "circle", label: "step.waiting" },
  in_progress: { tone: "info", icon: "play", label: "step.in_progress" },
  done: { tone: "success", icon: "check", label: "step.done" },
  failed: { tone: "danger", icon: "x", label: "step.failed" },
  blocked: { tone: "danger", icon: "ban", label: "step.blocked" },
  paused: { tone: "neutral", icon: "pause", label: "step.paused" },
  cancelled: { tone: "neutral", icon: "ban", label: "step.cancelled" },
};

export const REQUIREMENT_VIEW: Record<RequirementState, StatusView> = {
  pending: { tone: "warning", icon: "clock", label: "requirement.pending" },
  planned: { tone: "info", icon: "circle", label: "requirement.planned" },
  covered: { tone: "success", icon: "check", label: "requirement.covered" },
  ambiguous: { tone: "warning", icon: "alert", label: "requirement.ambiguous" },
  not_implementable: { tone: "danger", icon: "ban", label: "requirement.not_implementable" },
  superseded: { tone: "neutral", icon: "circle", label: "requirement.superseded" },
};

export const EXTRACTION_VIEW: Record<string, StatusView> = {
  complete: { tone: "success", icon: "check", label: "extraction.complete" },
  partial: { tone: "warning", icon: "alert", label: "extraction.partial" },
  failed: { tone: "danger", icon: "x", label: "extraction.failed" },
  unsupported: { tone: "danger", icon: "ban", label: "extraction.unsupported" },
};

export const RISK_VIEW: Record<string, StatusView> = {
  low: { tone: "neutral", icon: "dot", label: "risk.low" },
  medium: { tone: "info", icon: "dot", label: "risk.medium" },
  high: { tone: "warning", icon: "alert", label: "risk.high" },
  critical: { tone: "danger", icon: "alert", label: "risk.critical" },
};

/** Task status view; the contract types status as string, unknown values degrade to neutral. */
export function statusView(status: string): StatusView {
  return TASK_STATUS_VIEW[status as TaskStatus] ?? { tone: "neutral", icon: "circle", label: "status.backlog" };
}
